// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/imageCompress.ts
// 🔧 PURPOSE: Rimpicciolisce le foto della chat prima di caricarle.
//
//             Una foto del telefono pesa 3-8 MB e ha 12+ megapixel: su una
//             rete mobile in vacanza il caricamento durerebbe un'eternita', e
//             allo staff basta molto meno per capire "il rubinetto perde".
//             Qui la foto diventa un JPEG di al massimo 1600px di lato, senza
//             librerie: il browser la decodifica e la ridisegna su un canvas.
//             Se qualcosa va storto si carica l'originale: meglio una foto
//             pesante che nessuna foto.
// ============================================

export interface CompressOptions {
  /** Lato lungo massimo, in pixel. */
  maxSide?: number;
  /** Qualita' JPEG, da 0 a 1. */
  quality?: number;
}

const DEFAULT_MAX_SIDE = 1600;
const DEFAULT_QUALITY = 0.82;

// Sotto questo peso una foto in un formato che il server accetta non si
// tocca: ricomprimerla farebbe solo perdere qualita'.
const SMALL_ENOUGH_BYTES = 400 * 1024;
const PASSTHROUGH_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * HEIC/HEIF (il formato degli iPhone): solo Safari lo sa decodificare, e
 * non sempre. Non lo si comprime: si carica com'e' e lo converte Cloudinary.
 */
export function isHeic(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.indexOf('image/heic') === 0 || type.indexOf('image/heif') === 0) return true;
  return /\.(heic|heif)$/i.test(file.name || '');
}

interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

// Solo quello che serve del contesto 2D, comune a canvas e OffscreenCanvas.
type Ctx2D = Pick<
  CanvasRenderingContext2D,
  'fillStyle' | 'fillRect' | 'drawImage' | 'imageSmoothingEnabled' | 'imageSmoothingQuality'
>;

// OffscreenCanvas descritto a mano: i tipi di TypeScript 4.9 non conoscono
// convertToBlob, e molti browser non hanno OffscreenCanvas affatto.
interface OffscreenLike {
  width: number;
  height: number;
  getContext(contextId: '2d'): unknown;
  convertToBlob?: (options?: { type?: string; quality?: number }) => Promise<Blob>;
}
type OffscreenCtor = new (width: number, height: number) => OffscreenLike;

/**
 * Decodifica con createImageBitmap chiedendo di ruotare la foto secondo
 * l'EXIF ('from-image'). I browser che non conoscono quel valore lanciano un
 * errore: si ripiega sull'<img>, che l'orientamento EXIF lo applica da solo.
 */
async function decodeWithBitmap(file: Blob): Promise<DecodedImage | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    const options = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;
    const bitmap = await createImageBitmap(file, options);
    if (!bitmap.width || !bitmap.height) {
      bitmap.close();
      return null;
    }
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      release: () => {
        try { bitmap.close(); } catch { /* gia' chiusa */ }
      },
    };
  } catch {
    return null;
  }
}

/** Ripiego: <img> con un object URL, rilasciato quando si e' finito di disegnare. */
function decodeWithImg(file: Blob): Promise<DecodedImage | null> {
  return new Promise((resolve) => {
    let url = '';
    try {
      url = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (!width || !height) {
        URL.revokeObjectURL(url);
        resolve(null);
        return;
      }
      resolve({ source: img, width, height, release: () => URL.revokeObjectURL(url) });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

function paint(ctx: Ctx2D, source: CanvasImageSource, width: number, height: number): void {
  // Il JPEG non ha trasparenza: un PNG trasparente verrebbe nero. Fondo bianco.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
}

/** Disegna e codifica in JPEG: OffscreenCanvas se c'e', altrimenti un canvas normale. */
async function drawAndEncode(img: DecodedImage, width: number, height: number, quality: number): Promise<Blob | null> {
  const Offscreen = typeof window !== 'undefined'
    ? (window as unknown as { OffscreenCanvas?: OffscreenCtor }).OffscreenCanvas
    : undefined;
  if (typeof Offscreen === 'function') {
    try {
      const oc = new Offscreen(width, height);
      const ctx = oc.getContext('2d') as Ctx2D | null;
      if (ctx && typeof oc.convertToBlob === 'function') {
        paint(ctx, img.source, width, height);
        const blob = await oc.convertToBlob({ type: 'image/jpeg', quality });
        if (blob && blob.size > 0) return blob;
      }
    } catch {
      /* si riprova col canvas normale */
    }
  }

  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  try {
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    paint(ctx, img.source, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      try {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      } catch {
        resolve(null);
      }
    });
    return blob && blob.size > 0 ? blob : null;
  } catch {
    return null;
  } finally {
    // Su iOS la memoria dei canvas si libera solo azzerandoli.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * La foto pronta da caricare: un JPEG al massimo di `maxSide` px di lato,
 * oppure il file originale quando non conviene (o non si riesce) a
 * comprimerlo. Non lancia mai errori.
 */
export async function compressImage(file: File, options: CompressOptions = {}): Promise<Blob> {
  const maxSide = options.maxSide && options.maxSide > 0 ? options.maxSide : DEFAULT_MAX_SIDE;
  const quality = typeof options.quality === 'number' && options.quality > 0 && options.quality <= 1
    ? options.quality
    : DEFAULT_QUALITY;
  const type = (file.type || '').toLowerCase();
  const passthrough = PASSTHROUGH_TYPES.indexOf(type) !== -1;

  if (isHeic(file)) return file;
  if (passthrough && file.size < SMALL_ENOUGH_BYTES) return file;

  let decoded: DecodedImage | null = null;
  try {
    decoded = await decodeWithBitmap(file);
    if (!decoded) decoded = await decodeWithImg(file);
    if (!decoded) return file;

    const scale = Math.min(1, maxSide / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));
    const blob = await drawAndEncode(decoded, width, height, quality);
    if (!blob) return file;
    // Foto gia' piccola e ben compressa: la "compressa" peserebbe di piu'.
    if (scale === 1 && passthrough && blob.size >= file.size) return file;
    return blob;
  } catch {
    return file;
  } finally {
    if (decoded) decoded.release();
  }
}
